/* eslint-disable react/jsx-props-no-spreading */
import {
  Box,
  Button,
  Heading,
  Input,
  Select,
  Stack,
  VStack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { EducationHistoryGrade, JobSeeker } from '~/generated';
import {
  EducationMajorSelect,
  EducationalInstitutionSelect,
  InputContainer,
} from '~/components/molecules';
import {
  grades,
  prefecturesForProfile,
  profileStatus,
} from '~/i18n/domainMessages';
import { myProfile, updateProfile } from '~/queries';

import { notifySaveFailure } from '~/components/atoms';

import IntroductionSuccess from '~/components/organisms/IntroductionSuccess';
import { endOfMonth } from 'date-fns';
import {
  ISODateTimeToDateString,
  parseISODateString,
} from '~/utilities/formatDatetime';

const gradeOptions = Object.entries(grades).map(([k, v]) => ({
  id: k,
  name: v,
}));

const prefectureArray = Object.entries(prefecturesForProfile).map(([k, v]) => ({
  id: k,
  name: v,
}));

const profileStatusSelect = Object.entries(profileStatus).map(([k, v]) => ({
  id: k,
  name: v,
}));

type CreateProfileInput = {
  birthday: string;
  graduatedOn: string;
  address: keyof typeof prefecturesForProfile;
  grade: EducationHistoryGrade;
  name: string;
  faculty: string;
  department: string;
  major: string;
  status: string;
};

const CreateProfile: React.FC = () => {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { isValid, errors },
  } = useForm<CreateProfileInput>({
    mode: 'onChange',
  });

  const { loading } = useQuery<{ jobSeekerMe: JobSeeker }>(myProfile, {
    onCompleted: (data) => {
      // 一回入力した場合は通常のプロフィール編集画面に移動させる
      if (data.jobSeekerMe?.educationHistory != null) {
        navigate('/profile');
      }
    },
  });

  const [updateSeekerProfile, { error, loading: submitting }] = useMutation(
    updateProfile,
    { onCompleted: () => navigate('/profile') },
  );

  const currentYear = new Date().getFullYear();

  const hasFilledRequiredSelects = () =>
    !!(getValues('name') && getValues('major'));
  const onSubmit: SubmitHandler<CreateProfileInput> = async (input) => {
    const {
      graduatedOn,
      grade,
      name,
      faculty,
      department,
      major,
      ...jobSeeker
    } = input;

    const graduatedOnDate = ISODateTimeToDateString(
      endOfMonth(parseISODateString(graduatedOn)),
    );
    const birthday = ISODateTimeToDateString(jobSeeker.birthday);
    try {
      await updateSeekerProfile({
        variables: {
          input: {
            ...jobSeeker,
            birthday,
            educationHistory: {
              graduatedOn: graduatedOnDate,
              grade,
              name,
              faculty,
              department,
              major,
            },
          },
        },
      });
    } catch {
      reset();
      notifySaveFailure();
    }

    if (error) {
      reset();
      notifySaveFailure();
    }
  };

  return (
    <Box
      bg={useColorModeValue('gray.50', 'inherit')}
      px={5}
      py={10}
      display="flex"
      justifyContent="center"
    >
      <VStack spacing={5}>
        <Box>
          <Heading
            fontSize="lg"
            fontWeight="bold"
            lineHeight="6"
            textAlign="center"
          >
            プロフィール情報の入力
          </Heading>
        </Box>
        <Box textAlign="center" maxW="600px">
          <Text>リケイマッチのご登録ありがとうございます！</Text>
          <Text>
            以下の項目を入力頂くことで本登録が完了して、採用担当からスカウトが届くようになります。
          </Text>
        </Box>
        <IntroductionSuccess />
        <Box
          shadow="base"
          rounded={[null, 'md']}
          overflow={{ sm: 'hidden' }}
          maxW="600px"
        >
          <Stack
            px={4}
            py={5}
            bg={useColorModeValue('white', 'gray.700')}
            spacing={6}
            p={{ sm: 8 }}
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <InputContainer
                name="birthday"
                label="生年月日"
                invalid={!!errors.birthday}
              >
                <Input
                  w="200px"
                  marginBottom="5px"
                  type="date"
                  {...register('birthday', {
                    required: true,
                    min: `${currentYear - 30}-01-01`,
                    max: `${currentYear - 16}-12-12`,
                  })}
                  isRequired
                  isInvalid={!!errors.birthday}
                />
              </InputContainer>

              <InputContainer
                name="graduatedOn"
                label="卒業（見込み）"
                invalid={!!errors.graduatedOn}
              >
                <Input
                  w="200px"
                  marginBottom="5px"
                  type="month"
                  {...register('graduatedOn', {
                    required: true,
                    min: `${currentYear - 2}-03`,
                    max: `${currentYear + 6}-08`,
                  })}
                  isRequired
                  isInvalid={!!errors.graduatedOn}
                />
              </InputContainer>

              <InputContainer
                name="address"
                label="現住所"
                invalid={!!errors.address}
              >
                <Select
                  marginBottom="5px"
                  placeholder="-"
                  w="300px"
                  isRequired
                  isInvalid={!!errors.address}
                  {...register('address', { required: true })}
                >
                  {prefectureArray.map((prefecture) => (
                    <option key={prefecture.id} value={prefecture.id}>
                      {prefecture.name}
                    </option>
                  ))}
                </Select>
              </InputContainer>

              <InputContainer
                name="grade"
                label="学年"
                invalid={!!errors.grade}
              >
                <Select
                  w="200px"
                  marginBottom="10px"
                  {...register('grade', { required: true })}
                  placeholder="-"
                  isInvalid={!!errors.grade}
                  isRequired
                >
                  {gradeOptions.map((grade) => (
                    <option key={grade.id} value={grade.id}>
                      {grade.name}
                    </option>
                  ))}
                </Select>
              </InputContainer>

              <InputContainer
                name="name"
                label="学校名"
                invalid={!!errors.name}
              >
                <EducationalInstitutionSelect
                  onChange={(value) => {
                    setValue('name', value, { shouldDirty: true });
                  }}
                />
              </InputContainer>

              <InputContainer
                name="faculty"
                label="学部"
                invalid={!!errors.faculty}
              >
                <Input
                  w="200px"
                  marginBottom="10px"
                  {...register('faculty', { required: true })}
                  isInvalid={!!errors.faculty}
                />
              </InputContainer>

              <InputContainer
                name="department"
                label="学科"
                invalid={!!errors.department}
              >
                <Input
                  w="200px"
                  marginBottom="10px"
                  {...register('department', { required: true })}
                  isInvalid={!!errors.department}
                />
              </InputContainer>

              <InputContainer
                name="major"
                label="専攻分野（特に力を入れた分野を１つお選びください）"
                invalid={!!errors.major}
              >
                <EducationMajorSelect
                  onChange={(value) => {
                    setValue('major', value, { shouldDirty: true });
                  }}
                />
              </InputContainer>

              <InputContainer
                name="status"
                label="就活状況"
                invalid={!!errors.status}
              >
                <Select
                  placeholder="-"
                  w="300px"
                  isRequired
                  isInvalid={!!errors.status}
                  {...register('status', { required: true })}
                >
                  {profileStatusSelect.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.name}
                    </option>
                  ))}
                </Select>
              </InputContainer>

              <Button
                colorScheme="blue"
                isLoading={loading || submitting}
                disabled={!isValid || !hasFilledRequiredSelects()}
                type="submit"
              >
                登録完了
              </Button>
            </form>

            <Box>
              <Text>
                登録お疲れさまでした！この後、プロフィール画面へ移行します。
              </Text>
              <Text>
                あなたの個性が企業に伝わるよう、入力可能なところから入力してあなたならではのプロフィールを作成しましょう！
              </Text>
            </Box>
          </Stack>
        </Box>
      </VStack>
    </Box>
  );
};

export default CreateProfile;
